import React, { useState } from 'react';
import { Dayjs } from 'dayjs';
import { Row, Col, Empty } from 'antd';
import { Appointment } from '../../services/appointmentApi';
import { ShopOverviewCard } from './ShopOverviewCard';
import { EmployeeCard } from './EmployeeCard';
import { EmployeeDetailModal } from './EmployeeDetailModal';
import './EmployeeCardGrid.css';

interface Employee {
  id: number;
  first_name: string;
  last_name: string;
  role: string;
}

interface TechnicianSchedule {
  id: number;
  user_id: number;
  day_of_week: number;
  start_time: string;
  end_time: string;
  is_active: boolean;
}

interface ScheduleException {
  id: number;
  user_id: number;
  exception_type: 'time_off' | 'break' | 'special_hours' | 'holiday';
  start_datetime: string;
  end_datetime: string;
  reason?: string;
}

interface EmployeeCardGridProps {
  date: Dayjs;
  employees: Employee[];
  appointments: Appointment[];
  schedules: Map<number, TechnicianSchedule[]>;
  exceptions: ScheduleException[];
  onAppointmentClick: (appointment: Appointment) => void;
  onScheduleApprovedEstimate: () => void;
}

export const EmployeeCardGrid: React.FC<EmployeeCardGridProps> = ({
  date,
  employees,
  appointments,
  schedules,
  exceptions,
  onAppointmentClick,
  onScheduleApprovedEstimate
}) => {
  const [selectedEmployee, setSelectedEmployee] = useState<Employee | null>(null);
  const [modalVisible, setModalVisible] = useState(false);

  const handleCardClick = (employee: Employee) => {
    setSelectedEmployee(employee);
    setModalVisible(true);
  };

  const handleModalClose = () => {
    setModalVisible(false);
    setSelectedEmployee(null);
  };

  // Get appointments for selected employee
  const getEmployeeAppointments = (employeeId: number) => {
    return appointments.filter(apt => {
      if (apt.technician_id === employeeId) return true;
      if (apt.technicians?.some(t => t.technician_id === employeeId)) return true;
      return false;
    });
  };

  if (employees.length === 0) {
    return (
      <div className="employee-card-grid-empty">
        <Empty
          description="No employees available. Please add technicians or owners to schedule appointments."
          image={Empty.PRESENTED_IMAGE_SIMPLE}
        />
      </div>
    );
  }

  return (
    <>
      <div className="employee-card-grid">
        <Row gutter={[16, 16]}>
          {/* Shop Overview Card - Full Width */}
          <Col xs={24}>
            <ShopOverviewCard
              date={date}
              employees={employees}
              appointments={appointments}
              schedules={schedules}
            />
          </Col>

          {/* Employee Cards */}
          {employees.map(employee => {
            const employeeAppointments = getEmployeeAppointments(employee.id);
            const employeeSchedule = schedules.get(employee.id) || [];
            const employeeExceptions = exceptions.filter(exc => exc.user_id === employee.id);

            return (
              <Col key={employee.id} xs={24} sm={24} md={24} lg={12} xl={8} xxl={6}>
                <EmployeeCard
                  employee={employee}
                  date={date}
                  appointments={employeeAppointments}
                  schedule={employeeSchedule}
                  exceptions={employeeExceptions}
                  onClick={() => handleCardClick(employee)}
                />
              </Col>
            );
          })}
        </Row>
      </div>

      {/* Employee Detail Modal */}
      {selectedEmployee && (
        <EmployeeDetailModal
          visible={modalVisible}
          employee={selectedEmployee}
          date={date}
          appointments={getEmployeeAppointments(selectedEmployee.id)}
          schedule={schedules.get(selectedEmployee.id) || []}
          exceptions={exceptions.filter(exc => exc.user_id === selectedEmployee.id)}
          onClose={handleModalClose}
          onAppointmentClick={onAppointmentClick}
          onScheduleApprovedEstimate={onScheduleApprovedEstimate}
        />
      )}
    </>
  );
};
